/**
 * DialogNodeContext - Tracks selected node in Dialog flow mode
 * 
 * This context maintains the currently selected node when viewing a dialog in flow mode,
 * allowing the sidebar to display and edit the selected node's properties.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { DialogNode } from '@/lib/stores/dialogStore';

interface DialogNodeContextValue {
  /** ID of the currently selected node (flow node ID) */
  selectedNodeId: string | null;
  /** Full data of the currently selected node */
  selectedNodeData: DialogNode | null;
  /** Node index in the sections/nodes array */
  selectedNodeIndex: number | null;
  /** Update the selected node */
  setSelectedNode: (nodeId: string | null, nodeData: DialogNode | null, nodeIndex: number | null) => void;
  /** Clear the selection */
  clearSelectedNode: () => void;
  /** Handler for updating node data */
  onNodeUpdate?: (nodeId: string, updates: Partial<DialogNode>) => void;
  /** Set the node update handler */
  setOnNodeUpdate: (handler: (nodeId: string, updates: Partial<DialogNode>) => void) => void;
  /** Available nodes for dropdowns */
  availableNodes: Array<{ node_identifier: string; name?: string }>;
  setAvailableNodes: (nodes: Array<{ node_identifier: string; name?: string }>) => void;
  /** Available dialogs for jump options */
  availableDialogs: Array<{ id: string; name: string }>;
  setAvailableDialogs: (dialogs: Array<{ id: string; name: string }>) => void;
}

const DialogNodeContext = createContext<DialogNodeContextValue | undefined>(undefined);

interface DialogNodeProviderProps {
  children: ReactNode;
}

export function DialogNodeProvider({ children }: DialogNodeProviderProps) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedNodeData, setSelectedNodeData] = useState<DialogNode | null>(null);
  const [selectedNodeIndex, setSelectedNodeIndex] = useState<number | null>(null);
  const [onNodeUpdate, setOnNodeUpdateInternal] = useState<((nodeId: string, updates: Partial<DialogNode>) => void) | undefined>(undefined);
  const [availableNodes, setAvailableNodes] = useState<Array<{ node_identifier: string; name?: string }>>([]);
  const [availableDialogs, setAvailableDialogs] = useState<Array<{ id: string; name: string }>>([]);

  const setSelectedNode = useCallback((
    nodeId: string | null, 
    nodeData: DialogNode | null,
    nodeIndex: number | null
  ) => {
    setSelectedNodeId(nodeId);
    setSelectedNodeData(nodeData);
    setSelectedNodeIndex(nodeIndex);
  }, []);

  const clearSelectedNode = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedNodeData(null);
    setSelectedNodeIndex(null);
  }, []);

  const setOnNodeUpdate = useCallback((handler: (nodeId: string, updates: Partial<DialogNode>) => void) => {
    setOnNodeUpdateInternal(() => handler);
  }, []);

  const value: DialogNodeContextValue = {
    selectedNodeId,
    selectedNodeData,
    selectedNodeIndex,
    setSelectedNode,
    clearSelectedNode,
    onNodeUpdate,
    setOnNodeUpdate,
    availableNodes,
    setAvailableNodes,
    availableDialogs,
    setAvailableDialogs,
  };

  return (
    <DialogNodeContext.Provider value={value}>
      {children}
    </DialogNodeContext.Provider>
  );
}

/**
 * Hook to access dialog node context
 * @throws Error if used outside DialogNodeProvider
 */
export function useDialogNodeContext(): DialogNodeContextValue {
  const context = useContext(DialogNodeContext);
  if (!context) {
    throw new Error('useDialogNodeContext must be used within a DialogNodeProvider');
  }
  return context;
}

/**
 * Optional hook to access dialog node context
 * Returns undefined if used outside DialogNodeProvider
 */
export function useDialogNodeContextOptional(): DialogNodeContextValue | undefined {
  return useContext(DialogNodeContext);
}
